import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { formatPrice } from "@/utils/currency";

export function AccountingReport({ accountingData, onDownloadCsv, getCsvParams }) {
  const { t } = useTranslation();
  const accTotals = accountingData?.totals || { orders: 0, revenue: 0, tax: 0, discount: 0, net_revenue: 0 };

  return (
    <div className="space-y-4" data-testid="accounting-report">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-card border-border/50 text-center">
          <CardContent className="py-4">
            <p className="text-sm text-muted-foreground">{t("reports.accounting.orders")}</p>
            <p className="text-2xl font-bold" data-testid="acc-orders">{accTotals.orders}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50 text-center">
          <CardContent className="py-4">
            <p className="text-sm text-muted-foreground">{t("reports.accounting.grossRevenue")}</p>
            <p className="text-2xl font-bold" data-testid="acc-revenue">{formatPrice(accTotals.revenue)}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50 text-center">
          <CardContent className="py-4">
            <p className="text-sm text-muted-foreground">{t("reports.accounting.taxes")}</p>
            <p className="text-2xl font-bold" data-testid="acc-tax">{formatPrice(accTotals.tax)}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50 text-center">
          <CardContent className="py-4">
            <p className="text-sm text-muted-foreground">{t("reports.accounting.discounts")}</p>
            <p className="text-2xl font-bold" data-testid="acc-discount">{formatPrice(accTotals.discount)}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border/50 text-center">
          <CardContent className="py-4">
            <p className="text-sm text-muted-foreground">{t("reports.accounting.netRevenue")}</p>
            <p className="text-2xl font-bold text-green-500" data-testid="acc-net">{formatPrice(accTotals.net_revenue)}</p>
          </CardContent>
        </Card>
      </div>

      {accountingData?.by_payment?.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader><CardTitle className="text-base uppercase tracking-tight">{t("reports.accounting.byPaymentMethod")}</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {accountingData.by_payment.map(p => (
                <div key={p.method} className="flex items-center justify-between">
                  <Badge variant="outline">{p.method}</Badge>
                  <div className="text-right">
                    <p className="font-bold">{formatPrice(p.revenue)}</p>
                    <p className="text-xs text-muted-foreground">{p.count} {t("reports.orders.title").toLowerCase()}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {accountingData?.daily?.length > 0 && (
        <Card className="bg-card border-border/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm uppercase text-muted-foreground">{t("reports.accounting.dailyRevenue")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={accountingData.daily.map(d => ({ date: d._id?.slice(5), receita: Math.round(d.revenue) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
                  <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#6b7280" }} />
                  <YAxis tick={{ fontSize: 10, fill: "#6b7280" }} />
                  <Tooltip />
                  <Bar dataKey="receita" fill="#16a34a" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-3">
        <Button
          className="gap-2"
          onClick={() => onDownloadCsv(`/reports/accounting?${getCsvParams()}`, "contabilidade.csv")}
          data-testid="download-accounting-csv"
        >
          <FileSpreadsheet className="w-4 h-4" /> {t("reports.accounting.exportCsv")}
        </Button>
      </div>
    </div>
  );
}
